import { useEffect, useRef, useState } from "react";
import {
  FaSearch,
  FaUserTie,
  FaTags,
  FaBuilding,
  FaHeadset,
  FaHandshake,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationCircle,
  FaCommentDots,
  FaChevronRight,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
} from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";
import api from "../api/axios";

const API_URL = import.meta.env.VITE_API_URL || "https://api.planbjobs.in/api";

// TODO: replace with the approved business details
const CONTACT = {
  email: "support@planbjobs.in",
  phone: "+91 7904949969",
  address: "", // leave empty to hide until approved for publication
  social: [
    { label: "Facebook", url: "", Icon: FaFacebookF },
    { label: "Twitter", url: "", Icon: FaTwitter },
    { label: "LinkedIn", url: "", Icon: FaLinkedinIn },
    { label: "Instagram", url: "", Icon: FaInstagram },
  ],
};

const GRAD = "bg-gradient-to-r from-[#6D4AFF] to-[#FF4F8B]";

const CATEGORIES = [
  { type: "Job seeker support", Icon: FaSearch, tint: "bg-purple-50 text-purple-600", company: false },
  { type: "Employer registration", Icon: FaUserTie, tint: "bg-pink-50 text-pink-600", company: true },
  { type: "Pricing and packages", Icon: FaTags, tint: "bg-amber-50 text-amber-600", company: true },
  { type: "Corporate recruitment", Icon: FaBuilding, tint: "bg-blue-50 text-blue-600", company: true },
  { type: "Technical support", Icon: FaHeadset, tint: "bg-rose-50 text-rose-500", company: false },
  { type: "Partnership enquiries", Icon: FaHandshake, tint: "bg-emerald-50 text-emerald-600", company: true },
];

const COMPANY_TYPES = CATEGORIES.filter((c) => c.company).map((c) => c.type);
const MAX_MSG = 1000;

const initialForm = {
  full_name: "",
  email: "",
  mobile: "",
  enquiry_type: "",
  company_name: "",
  message: "",
  consent: false,
  website: "", // honeypot, must stay empty
};

const cleanMobile = (v) => v.replace(/[\s-]/g, "").replace(/^(\+91|91|0)/, "");

const validateField = (name, form) => {
  switch (name) {
    case "full_name":
      return form.full_name.trim().length < 2 ? "Please enter your full name." : "";
    case "email":
      return /^\S+@\S+\.\S+$/.test(form.email) ? "" : "Please enter a valid email address.";
    case "mobile":
      return /^[6-9]\d{9}$/.test(cleanMobile(form.mobile)) ? "" : "Enter a valid 10-digit mobile number.";
    case "enquiry_type":
      return form.enquiry_type ? "" : "Please choose what your enquiry is about.";
    case "company_name":
      return COMPANY_TYPES.includes(form.enquiry_type) && !form.company_name.trim()
        ? "Please enter your company name."
        : "";
    case "message":
      return form.message.trim().length < 10 ? "Please write at least 10 characters." : "";
    case "consent":
      return form.consent ? "" : "Please accept to continue.";
    default:
      return "";
  }
};

const FIELDS = ["full_name", "email", "mobile", "enquiry_type", "company_name", "message", "consent"];

export default function ContactPage() {
  const [user] = useState(() => {
    try {
      const s = localStorage.getItem("user");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [serverMsg, setServerMsg] = useState("");
  const startedAt = useRef(Date.now());
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "Contact PlanB Jobs – Employer & Candidate Support";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Contact PlanB Jobs for job seeker assistance, employer registration, recruitment packages, technical support and business enquiries.";
  }, []);

  const showCompany = COMPANY_TYPES.includes(form.enquiry_type);

  const update = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      setErrors((er) => {
        const out = { ...er };
        Object.keys(patch).forEach((k) => {
          if (touched[k] || er[k]) out[k] = validateField(k, next);
        });
        if (patch.enquiry_type !== undefined && (touched.company_name || er.company_name)) {
          out.company_name = validateField("company_name", next);
        }
        return out;
      });
      return next;
    });
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    update({ [name]: type === "checkbox" ? checked : value });
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((er) => ({ ...er, [name]: validateField(name, form) }));
  };

  const pickCategory = (type) => {
    update({ enquiry_type: type });
    setTouched((t) => ({ ...t, enquiry_type: true }));
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = {};
    FIELDS.forEach((f) => {
      const m = validateField(f, form);
      if (m) er[f] = m;
    });
    setErrors(er);
    setTouched(Object.fromEntries(FIELDS.map((f) => [f, true])));
    if (Object.keys(er).length) {
      const first = FIELDS.find((f) => er[f]);
      document.getElementById(`cf-${first}`)?.focus();
      return;
    }

    setStatus("sending");
    setServerMsg("");
    try {
      await api.post("/contact", {
        ...form,
        company_name: showCompany ? form.company_name : "",
        elapsed_ms: Date.now() - startedAt.current,
      });
      setStatus("success");
      setForm(initialForm);
      setErrors({});
      setTouched({});
      startedAt.current = Date.now();
      scrollToForm();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 422 && data?.errors) {
        const mapped = {};
        Object.keys(data.errors).forEach((k) => (mapped[k] = data.errors[k][0]));
        setErrors(mapped);
        setStatus("idle");
      } else if (err.response) {
        setServerMsg(data?.message || "Something went wrong. Please try again.");
        setStatus("error");
      } else {
        setServerMsg("Network error. Please check your connection and try again.");
        setStatus("error");
      }
    }
  };

  const socials = CONTACT.social.filter((s) => s.url);

  const inputCls = (name) =>
    `w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-gray-800 placeholder:text-gray-400 transition focus:outline-none focus:ring-2 ${
      errors[name]
        ? "border-red-300 focus:ring-red-200"
        : "border-gray-200 hover:border-purple-300 focus:border-transparent focus:ring-purple-400"
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <Navbar user={user} />

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        {/* Banner */}
        <section className={`relative overflow-hidden rounded-3xl ${GRAD} px-6 py-10 text-white shadow-lg shadow-purple-200 md:px-12 md:py-14`}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-white/10" />
          <p className="text-sm font-semibold uppercase tracking-widest text-white/80">Contact us</p>
          <h1 className="mt-2 max-w-2xl text-3xl font-extrabold leading-tight md:text-5xl">
            Let's Talk About Your Next Move.
          </h1>
          <p className="mt-3 max-w-2xl text-base text-white/90 md:text-lg">
            Need help finding a job? Looking to hire? Have questions about PlanB Jobs? We're here to help.
          </p>
        </section>

        {/* Quick action cards */}
        <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <button
            type="button"
            onClick={scrollToForm}
            className={`flex items-center gap-4 rounded-2xl ${GRAD} p-5 text-left text-white shadow-md shadow-pink-200 transition hover:-translate-y-0.5 hover:shadow-lg`}
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20 text-lg">
              <FaCommentDots />
            </span>
            <span>
              <span className="block font-bold">Send a Message</span>
              <span className="block text-sm text-white/90">Fill the form below</span>
            </span>
          </button>

          <a
            href={`mailto:${CONTACT.email}`}
            className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-lg text-purple-600">
              <FaEnvelope />
            </span>
            <span className="min-w-0">
              <span className="block font-bold text-[#0D1B3D]">Email Us</span>
              <span className="block truncate text-sm text-gray-500">{CONTACT.email}</span>
            </span>
          </a>

          <a
            href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-lg text-purple-600">
              <FaPhoneAlt />
            </span>
            <span>
              <span className="block font-bold text-[#0D1B3D]">Call Us</span>
              <span className="block text-sm text-gray-500">{CONTACT.phone}</span>
            </span>
          </a>
        </section>

        {/* Sidebar + form */}
        <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          {/* Sidebar */}
          <aside className="space-y-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
              <p className="px-3 pb-2 pt-2 text-xs font-bold uppercase tracking-wider text-gray-400">
                What do you need help with?
              </p>
              <div className="space-y-1">
                {CATEGORIES.map(({ type, Icon, tint }) => {
                  const active = form.enquiry_type === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        pickCategory(type);
                        if (window.innerWidth < 1024) scrollToForm();
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition ${
                        active ? `${GRAD} text-white shadow-md shadow-pink-200` : "text-gray-700 hover:bg-purple-50"
                      }`}
                    >
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm ${
                          active ? "bg-white/20 text-white" : tint
                        }`}
                      >
                        <Icon />
                      </span>
                      <span className="flex-1">{type}</span>
                      <FaChevronRight className={`text-xs ${active ? "text-white/80" : "text-gray-300"}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Still need help card */}
            <div className={`rounded-2xl ${GRAD} p-6 text-white shadow-md shadow-pink-200`}>
              <h3 className="text-lg font-extrabold">Still need help?</h3>
              <p className="mt-1 text-sm text-white/90">Reach our team directly using the details below.</p>

              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <FaEnvelope className="mt-1 shrink-0" />
                  <a href={`mailto:${CONTACT.email}`} className="break-all font-semibold hover:underline">{CONTACT.email}</a>
                </li>
                <li className="flex items-start gap-3">
                  <FaPhoneAlt className="mt-1 shrink-0" />
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="font-semibold hover:underline">{CONTACT.phone}</a>
                </li>
                {CONTACT.address && (
                  <li className="flex items-start gap-3">
                    <FaMapMarkerAlt className="mt-1 shrink-0" />
                    <span className="font-semibold">{CONTACT.address}</span>
                  </li>
                )}
              </ul>

              {socials.length > 0 && (
                <div className="mt-5 flex gap-2 border-t border-white/20 pt-4">
                  {socials.map(({ label, url, Icon }) => (
                    <a
                      key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 transition hover:bg-white hover:text-[#6D4AFF]"
                    >
                      <Icon />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Form */}
          <div ref={formRef} className="scroll-mt-24 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center py-12 text-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-5xl text-emerald-500">
                  <FaCheckCircle />
                </span>
                <h2 className="mt-5 text-2xl font-extrabold text-[#0D1B3D]">Message sent!</h2>
                <p className="mt-2 max-w-sm text-gray-500">
                  Thank you for reaching out. Our team will review your message and get back to you shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className={`mt-6 rounded-full ${GRAD} px-6 py-3 text-sm font-semibold text-white shadow-md shadow-pink-200 transition hover:opacity-90`}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <h2 className="text-2xl font-extrabold text-[#6D28D9]">Send us a message</h2>
                <p className="mt-1 mb-6 text-sm text-gray-500">
                  Fill in the form and we'll get back to you. Fields marked * are required.
                </p>

                {/* Honeypot */}
                <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
                  <label>
                    Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
                  </label>
                </div>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <Field label="Full name *" id="cf-full_name" error={errors.full_name}>
                      <input
                        id="cf-full_name" name="full_name" value={form.full_name}
                        onChange={onChange} onBlur={onBlur} autoComplete="name"
                        placeholder="Your full name" className={inputCls("full_name")}
                      />
                    </Field>
                    <Field label="Mobile number *" id="cf-mobile" error={errors.mobile}>
                      <input
                        id="cf-mobile" type="tel" name="mobile" value={form.mobile}
                        onChange={onChange} onBlur={onBlur} autoComplete="tel" inputMode="numeric"
                        placeholder="10-digit mobile number" className={inputCls("mobile")}
                      />
                    </Field>
                  </div>

                  <Field label="Email *" id="cf-email" error={errors.email}>
                    <input
                      id="cf-email" type="email" name="email" value={form.email}
                      onChange={onChange} onBlur={onBlur} autoComplete="email"
                      placeholder="you@example.com" className={inputCls("email")}
                    />
                  </Field>

                  <Field label="Enquiry type *" id="cf-enquiry_type" error={errors.enquiry_type}>
                    <select
                      id="cf-enquiry_type" name="enquiry_type" value={form.enquiry_type}
                      onChange={onChange} onBlur={onBlur} className={inputCls("enquiry_type")}
                    >
                      <option value="">Select an option</option>
                      {CATEGORIES.map((c) => (
                        <option key={c.type} value={c.type}>{c.type}</option>
                      ))}
                    </select>
                  </Field>

                  {showCompany && (
                    <Field label="Company name *" id="cf-company_name" error={errors.company_name}>
                      <input
                        id="cf-company_name" name="company_name" value={form.company_name}
                        onChange={onChange} onBlur={onBlur} autoComplete="organization"
                        placeholder="Your company name" className={inputCls("company_name")}
                      />
                    </Field>
                  )}

                  <Field label="Message *" id="cf-message" error={errors.message} hint={`${form.message.length}/${MAX_MSG}`}>
                    <textarea
                      id="cf-message" name="message" rows={5} maxLength={MAX_MSG}
                      value={form.message} onChange={onChange} onBlur={onBlur}
                      placeholder="Tell us how we can help..." className={`${inputCls("message")} resize-y`}
                    />
                  </Field>

                  <div>
                    <label className="flex cursor-pointer items-start gap-3 text-sm text-gray-600">
                      <input
                        id="cf-consent" type="checkbox" name="consent" checked={form.consent}
                        onChange={onChange} onBlur={onBlur}
                        className="mt-1 h-4 w-4 rounded border-gray-300 accent-purple-600"
                      />
                      <span>
                        I agree to PlanB Jobs storing and using my details to respond to this enquiry, as described in the{" "}
                        <a href="/privacy-policy" className="font-semibold text-purple-600 hover:underline">Privacy Policy</a>.
                      </span>
                    </label>
                    {errors.consent && <ErrorText>{errors.consent}</ErrorText>}
                  </div>
                </div>

                {status === "error" && (
                  <div role="alert" className="mt-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <FaExclamationCircle className="mt-0.5 shrink-0" /> {serverMsg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full ${GRAD} px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-pink-200 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto`}
                >
                  {status === "sending" ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane /> Send message
                    </>
                  )}
                </button>

                <p className="mt-4 text-xs text-gray-400">
                  Your information is used only to respond to your enquiry and is never sold.
                </p>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, id, error, hint, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-semibold text-[#0D1B3D]">{label}</label>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {children}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}

function ErrorText({ children }) {
  return (
    <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600">
      <FaExclamationCircle className="shrink-0" /> {children}
    </p>
  );
}